import { useEffect, useState } from 'react';
import { useRealtimePollInterval } from '../../../realtime/useRealtimePollInterval';

/** How often the page asks again while the first read of a channel runs. */
export const FIRST_READ_POLL_MS = 3000;
/** After this the page stops asking. A reload shows the numbers of a read that took longer. */
export const FIRST_READ_POLL_TIMEOUT_MS = 2 * 60 * 1000;

/**
 * The polling interval of a query while a channel shows "Reading your numbers".
 *
 * The first read starts when a channel connects, and it takes a second or a few minutes. The
 * page asks again every 3 seconds, so the numbers show with no reload. It stops when no channel
 * reads any more, or after 2 minutes. 0 means no poll.
 *
 * While the realtime socket is connected, the `promo.channel.synced` event reads the channels
 * again when the read ends, and the page does not poll.
 */
export function useFirstReadPoll(isReading: boolean): number {
  const [isTimedOut, setIsTimedOut] = useState(false);

  useEffect(() => {
    if (!isReading) {
      setIsTimedOut(false);
      return undefined;
    }
    const timer = window.setTimeout(() => setIsTimedOut(true), FIRST_READ_POLL_TIMEOUT_MS);
    return () => window.clearTimeout(timer);
  }, [isReading]);

  return useRealtimePollInterval(isReading && !isTimedOut ? FIRST_READ_POLL_MS : 0);
}
